import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { FunctionComponent } from 'react';

import { Tooltip } from '../../../../../components/Tooltip';
import { CreateBedroomDrawer } from '../CreateBedroomDrawer/CreateBedroomDrawer';
import { DeleteBedroomDialog } from '../DeleteBedroomDialog/DeleteBedroomDialog';
import { EditBedroomDrawer } from '../EditBedroomDrawer/EditBedroomDrawer';
import { useBedroomsDrawer } from './useBedroomsDrawer';

interface BedroomsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  placeId: string | null;
}

export const BedroomsDrawer: FunctionComponent<BedroomsDrawerProps> = ({
  isOpen,
  onClose,
  placeId,
}) => {
  const {
    bedrooms,
    onCreateBedroom,
    isCreateBedroomDrawerOpen,
    onCreateBedroomDrawerClose,
    loading,
    onDeleteBedroom,
    onShowDeleteDialog,
    onCloseDeleteDialog,
    bedroomToDeleteId,
    editingBedroom,
    onCloseEditBedroom,
    onEditBedroom,
  } = useBedroomsDrawer(placeId);

  return (
    <>
      <Drawer anchor="right" open={isOpen} onClose={onClose}>
        <Box sx={{ mt: 10, mx: 2, width: 360 }}>
          <Typography variant="h4">{'Chambres du lieu'}</Typography>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {bedrooms.map((bedroom) => (
              <div key={bedroom.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    <>
                      <Tooltip title="Modifier la chambre">
                        <IconButton
                          color="info"
                          onClick={() => onEditBedroom(bedroom)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer la chambre">
                        <IconButton
                          color="error"
                          onClick={() => onShowDeleteDialog(bedroom.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  }
                >
                  <ListItemText
                    primary={bedroom.doorNumber}
                    secondary={
                      <Box flexDirection="column" display="flex">
                        <Typography
                          component="span"
                          variant="overline"
                          sx={{ color: 'text.secondary' }}
                        >
                          Étage: {bedroom.floor}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: 'text.primary', display: 'inline' }}
                        >
                          Places: {bedroom.maxUsers}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </div>
            ))}
            <Box display="flex" justifyContent="center" marginBottom={1}>
              {loading ? <CircularProgress /> : null}
            </Box>
          </List>
          <Button variant="outlined" fullWidth onClick={onCreateBedroom}>
            Ajouter une chambre
          </Button>
        </Box>
      </Drawer>
      {isCreateBedroomDrawerOpen ? (
        <CreateBedroomDrawer
          placeId={placeId}
          isOpen={isCreateBedroomDrawerOpen}
          onClose={onCreateBedroomDrawerClose}
          otherBedrooms={bedrooms}
        />
      ) : null}
      {editingBedroom ? (
        <EditBedroomDrawer
          bedroom={editingBedroom}
          isOpen={editingBedroom !== null}
          onClose={onCloseEditBedroom}
        />
      ) : null}
      <DeleteBedroomDialog
        onCancel={onCloseDeleteDialog}
        onDelete={onDeleteBedroom}
        open={bedroomToDeleteId !== null}
      />
    </>
  );
};
